import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from '@mui/material';
import {
  Upload,
  Refresh,
  CloudSync,
  Search,
  ShowChart,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  commitStockImport,
  getStockRegistryDashboard,
  previewStockImport,
  searchStocks,
  StockBreezeSyncResult,
  StockImportPreview,
  StockRegistryDashboard,
  StockRegistryView,
  syncStockBreeze,
  syncStockBreezeBulk,
} from '../../../services/request/StockRegistry';
import { getBreezeStatus } from '../../../services/request/BreezeWealth';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import StockBreezeDetailDialog from './StockBreezeDetailDialog';

const StockRegistryHub: React.FC = () => {
  const [dashboard, setDashboard] = useState<StockRegistryDashboard | null>(null);
  const [stocks, setStocks] = useState<StockRegistryView[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [exchange, setExchange] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [sourceExchange, setSourceExchange] = useState('NSE');
  const [preview, setPreview] = useState<StockImportPreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [breezeLinked, setBreezeLinked] = useState<boolean | null>(null);
  const [breezeDetail, setBreezeDetail] = useState<StockBreezeSyncResult | null>(null);
  const [breezeDetailOpen, setBreezeDetailOpen] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const d = await getStockRegistryDashboard();
      setDashboard(d);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load dashboard');
    }
  }, []);

  const loadStocks = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await searchStocks({
        q: q || undefined,
        status: status || undefined,
        exchange: exchange || undefined,
        page,
        size: rowsPerPage,
      });
      setStocks(res?.content || []);
      setTotal(res?.totalElements ?? 0);
    } catch (e: any) {
      toast.error(e?.message || 'Search failed');
      setStocks([]);
    } finally {
      setLoading(false);
    }
  }, [q, status, exchange, page, rowsPerPage]);

  useEffect(() => {
    loadDashboard();
    getBreezeStatus()
      .then((res: { data?: { hasSession?: boolean } }) => {
        setBreezeLinked(!!res?.data?.hasSession);
      })
      .catch(() => setBreezeLinked(false));
  }, [loadDashboard]);

  useEffect(() => {
    loadStocks();
  }, [loadStocks]);

  const handlePreviewImport = async () => {
    if (!importFile) {
      toast.error('Select a file first');
      return;
    }
    setImporting(true);
    try {
      const p = await previewStockImport(importFile, sourceExchange);
      setPreview(p);
      setPreviewOpen(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Preview failed');
    } finally {
      setImporting(false);
    }
  };

  const handleCommitImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const result: any = await commitStockImport(importFile, sourceExchange);
      toast.success(`Imported ${result?.successCount ?? 0} stocks`);
      setPreviewOpen(false);
      setImportFile(null);
      await loadDashboard();
      await loadStocks();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleBulkSync = async () => {
    setLoading(true);
    try {
      const r: any = await syncStockBreezeBulk(20);
      toast.success(`Synced ${r?.successCount ?? 0} stocks via ICICI Breeze`);
      await loadStocks();
    } catch (e: any) {
      toast.error(e?.message || 'Breeze sync failed — connect session in ICICI Breeze first');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ShowChart color="primary" />
          <Box>
            <Typography variant="h5">NSE / BSE Stock Registry</Typography>
            <Typography variant="body2" color="text.secondary">
              Master list for screening, analysis & ICICI Direct enrichment
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button startIcon={<Refresh />} onClick={() => { loadDashboard(); loadStocks(); }}>
            Refresh
          </Button>
          <Button startIcon={<CloudSync />} variant="outlined" onClick={handleBulkSync} disabled={loading}>
            Sync Breeze (20)
          </Button>
        </Box>
      </Box>

      {breezeLinked === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ICICI Breeze is not connected. Open{' '}
          <strong>ICICI Breeze</strong> in the sidebar, log in, and exchange your session before syncing stocks.
        </Alert>
      )}

      {dashboard && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard label="Total" value={String(dashboard.totalStocks)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard label="NSE" value={String(dashboard.nseStocks)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard label="BSE" value={String(dashboard.bseStocks)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard label="Active" value={String(dashboard.activeStocks)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard label="Suspended" value={String(dashboard.suspendedStocks)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard label="Delisted" value={String(dashboard.delistedStocks)} />
          </Grid>
        </Grid>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bulk upload (NSE JSON / Excel / CSV)
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <TextField select label="Source exchange" size="small" value={sourceExchange} onChange={(e) => setSourceExchange(e.target.value)} sx={{ minWidth: 120 }}>
              <MenuItem value="NSE">NSE</MenuItem>
              <MenuItem value="BSE">BSE</MenuItem>
            </TextField>
            <Button variant="outlined" component="label">
              Choose file
              <input type="file" hidden accept=".xlsx,.xls,.csv,.json" onChange={(e) => setImportFile(e.target.files?.[0] ?? null)} />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {importFile ? importFile.name : 'No file selected'}
            </Typography>
            <Button startIcon={<Upload />} variant="contained" onClick={handlePreviewImport} disabled={!importFile || importing}>
              Preview import
            </Button>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            NSE JSON (csvjson export): SYMBOL, NAME OF COMPANY, SERIES, DATE OF LISTING, ISIN NUMBER, FACE VALUE, MARKET LOT.
            Also supports Excel/CSV with the same column names. Rows merge on ISIN or NSE symbol.
          </Alert>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
            <TextField
              size="small"
              label="Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, opacity: 0.5 }} /> }}
              sx={{ minWidth: 220 }}
            />
            <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} sx={{ minWidth: 120 }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="DELISTED">Delisted</MenuItem>
            </TextField>
            <TextField select size="small" label="Exchange" value={exchange} onChange={(e) => { setExchange(e.target.value); setPage(0); }} sx={{ minWidth: 120 }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="NSE">NSE</MenuItem>
              <MenuItem value="BSE">BSE</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={() => { setPage(0); loadStocks(); }}>
              Apply
            </Button>
          </Box>
          {loading && <LinearProgress sx={{ mb: 1 }} />}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>NSE</TableCell>
                  <TableCell>BSE</TableCell>
                  <TableCell>ISIN</TableCell>
                  <TableCell>Sector</TableCell>
                  <TableCell>Cap</TableCell>
                  <TableCell align="right">LTP</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Quality</TableCell>
                  <TableCell align="center">Breeze</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stocks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No stocks in registry — upload NSE/BSE master file to get started
                    </TableCell>
                  </TableRow>
                ) : (
                  stocks.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.companyName}</TableCell>
                      <TableCell>{s.nseSymbol || '—'}</TableCell>
                      <TableCell>{s.bseSymbol || '—'}</TableCell>
                      <TableCell>{s.isin || '—'}</TableCell>
                      <TableCell>{s.sector || '—'}</TableCell>
                      <TableCell>{s.marketCapClass || '—'}</TableCell>
                      <TableCell align="right">
                        {s.enrichment?.ltp != null ? formatCurrency(Number(s.enrichment.ltp)) : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip label={s.status} size="small" color={s.status === 'ACTIVE' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>{s.dataQualityStatus || '—'}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          disabled={syncingId === s.id}
                          onClick={async () => {
                            setSyncingId(s.id);
                            try {
                              const result = await syncStockBreeze(s.id);
                              if (result.status === 'OK') {
                                toast.success('Breeze sync complete');
                                setBreezeDetail(result);
                                setBreezeDetailOpen(true);
                                loadStocks();
                              } else {
                                toast.error((result.errors || []).join(' ') || 'Sync failed');
                                setBreezeDetail(result);
                                setBreezeDetailOpen(true);
                              }
                            } catch (err: any) {
                              const msg =
                                err?.response?.data?.message ||
                                err?.response?.data?.data?.errors?.[0] ||
                                err?.message ||
                                'Sync failed';
                              toast.error(msg);
                            } finally {
                              setSyncingId(null);
                            }
                          }}
                        >
                          {syncingId === s.id ? '…' : 'Sync'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      <StockBreezeDetailDialog
        open={breezeDetailOpen}
        onClose={() => setBreezeDetailOpen(false)}
        data={breezeDetail}
      />

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import preview</DialogTitle>
        <DialogContent>
          {preview && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Total: {preview.totalRows} · Valid: {preview.validRows} · Invalid: {preview.invalidRows}
              </Typography>
              {preview.errors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2, maxHeight: 120, overflow: 'auto' }}>
                  {preview.errors.slice(0, 15).map((e, i) => (
                    <div key={i}>{e}</div>
                  ))}
                </Alert>
              )}
              <Typography variant="subtitle2">Sample rows (first 25)</Typography>
              <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 240 }}>
                {JSON.stringify(preview.previewRows?.slice(0, 5), null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCommitImport} disabled={!preview?.canImport || importing}>
            Import {preview?.validRows ?? 0} stocks
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6">{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default StockRegistryHub;
